import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  CheckCircle2,
  Circle,
  Flame,
  TrendingUp,
  MessageSquare,
  Calendar,
  Clock,
  RefreshCw,
  Sparkles,
  ArrowRight,
  ExternalLink,
  EyeOff
} from "lucide-react";
import { API_BASE_URL } from '../../config';
import { cn, normalizeTag } from '../../lib/utils';
import { usePostModal } from '../../contexts/PostModalContext';
import { getPlatformIcon } from '../../utils/platformIcons';
import { TrendMediaModal, TrendMediaMatch } from './TrendMediaModal';
import { DevlogMissionModal } from './DevlogMissionModal';
import { GameplayMissionModal } from './GameplayMissionModal';

interface Recommendation {
  id: string;
  type: 'TREND' | 'SEASONAL' | 'ENGAGEMENT' | 'CONTENT_PILLAR' | 'REPOST' | 'INACTIVITY' | 'MEME' | 'DEVLOG';
  title: string;
  suggestion: string;
  priority: number;
  status: 'PENDING' | 'USED' | 'DISMISSED';
  metadata?: any;
}

interface DailyMissionsProps {
  gameId: string;
  className?: string;
}

export const DailyMissions: React.FC<DailyMissionsProps> = ({ gameId, className }) => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [trendModalMission, setTrendModalMission] = useState<Recommendation | null>(null);
  const [devlogModalMission, setDevlogModalMission] = useState<Recommendation | null>(null);
  const [gameplayModalMission, setGameplayModalMission] = useState<Recommendation | null>(null);
  const { openPostModal } = usePostModal();
  const token = localStorage.getItem('token');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextUpdate = new Date();
      nextUpdate.setHours(24, 0, 0, 0); // Next midnight
      
      const diff = nextUpdate.getTime() - now.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeLeft(`${hours}:${minutes.toString().padStart(2, '0')}`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const fetchMissions = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/recommendations/${gameId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error('[DailyMissions] Error fetching missions:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (gameId) {
      fetchMissions();
    }
  }, [gameId]);

  const handleDismissMission = async (missionId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/recommendations/${missionId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(prev => prev.filter(r => r.id !== missionId));
    } catch (error) {
      console.error('[DailyMissions] Error dismissing mission:', error);
    }
  };

  const isCommentMission = (mission: Recommendation) =>
    mission.type === 'ENGAGEMENT' && mission.title.includes('Reply to Comments');

  const buildMissionPost = (mission: Recommendation, initialMedia?: any) => {
    let initialText = "";
    const metadata = mission.metadata || {};

    // Extract tags from mission
    let missionPriorityTags: string[] = [];
    let otherTags: string[] = [];

    const titleHashtags = mission.title.match(/#\w+/g);
    if (titleHashtags) {
      titleHashtags.forEach(tag => {
        const normalized = normalizeTag(tag);
        if (!missionPriorityTags.includes(normalized)) {
          missionPriorityTags.push(normalized);
        }
      });
    }

    if (mission.type === 'TREND' && metadata.trendName) {
      const cleanTrendName = metadata.trendName.replace(/[^a-zA-Z0-9]/g, '');
      const trendTag = metadata.trendName.startsWith('#')
        ? metadata.trendName
        : `#${cleanTrendName}`;
      if (!missionPriorityTags.includes(trendTag)) {
        missionPriorityTags.push(trendTag);
      }
    }

    const suggestionHashtags = mission.suggestion.match(/#\w+/g);
    if (suggestionHashtags) {
      suggestionHashtags.forEach(tag => {
        const normalized = normalizeTag(tag);
        if (!missionPriorityTags.includes(normalized) && !otherTags.includes(normalized)) {
          otherTags.push(normalized);
        }
      });
    }

    if (initialMedia?.aiMetadata?.tags) {
      initialMedia.aiMetadata.tags.forEach((tag: string) => {
        const normalized = normalizeTag(tag.startsWith('#') ? tag : `#${tag}`);
        if (!missionPriorityTags.includes(normalized) && !otherTags.includes(normalized)) {
          otherTags.push(normalized);
        }
      });
    }

    if (mission.type === 'CONTENT_PILLAR' && metadata.pillarId) {
      const pillarTags: Record<string, string[]> = {
        'vfx': ['#vfx', '#madewithunity', '#gamedev'],
        'gameplay': ['#gameplay', '#indiedev'],
        'behind': ['#behindthescenes', '#gamedev', '#wip']
      };
      const tagsForPillar = (pillarTags[metadata.pillarId] || []).map(t => normalizeTag(t));
      tagsForPillar.forEach(tag => {
        if (!missionPriorityTags.includes(tag) && !otherTags.includes(tag)) {
          otherTags.push(tag);
        }
      });
    }

    const finalTags = [...missionPriorityTags, ...otherTags].slice(0, 2);

    // Use AI suggested text if available
    if (initialMedia?.aiMetadata?.suggestedTexts) {
      initialText = initialMedia.aiMetadata.suggestedTexts.en?.[0] || initialMedia.aiMetadata.suggestedTexts.es?.[0] || "";
    }

    // Fallback texts
    if (!initialText) {
      const trendName = metadata.trendName || "";
      const hashtag = finalTags[0] || (trendName ? (trendName.startsWith('#') ? trendName : `#${trendName.replace(/[^a-zA-Z0-9]/g, '')}`) : "");

      switch (mission.type) {
        case 'TREND':
          if (trendName) {
            if (trendName.includes("//")) {
              initialText = `${trendName} ${hashtag}\n\n`;
            } else if (trendName.toLowerCase().includes("wishlist")) {
              initialText = `It's ${trendName}! \n\nWishlist my game here: `;
            } else if (trendName.toLowerCase().includes("thursday") || trendName.toLowerCase().includes("monday") || trendName.toLowerCase().includes("friday")) {
              initialText = `Happy ${trendName}! ${hashtag}\n\n`;
            } else {
              initialText = `Joining the ${trendName} trend! ${hashtag}\n\n#gamedev #indiedev`;
            }
          }
          break;
        case 'DEVLOG':
          initialText = "Devlog update: Working on [Topic] today!\n\n#indiedev #devlog #gamedev";
          break;
        case 'MEME':
          initialText = "That moment when [Gamedev Struggle]...\n\n#gamedev #meme #indiedev";
          break;
        case 'SEASONAL':
          initialText = `${mission.title}! Hope everyone is having a great day.\n\n#gamedev #indiedev`;
          break;
        case 'ENGAGEMENT':
          initialText = "Question for you all: What's your favorite [Game Mechanic/Feature]?\n\n#indiedev #gamedev";
          break;
        case 'CONTENT_PILLAR':
          if (metadata.pillarId === 'vfx') {
            initialText = "Adding some juice and VFX to the game! What do you think?\n\n#vfx #gamedev #indiedev";
          } else if (metadata.pillarId === 'gameplay') {
            initialText = "A quick gameplay clip! Progress is looking good.\n\n#gameplay #indiedev #gamedev";
          } else if (metadata.pillarId === 'behind') {
            initialText = "A look behind the scenes!\n\n#indiedev #gamedev #workinprogress";
          } else if (mission.title.includes('#')) {
            const hashtag = mission.title.match(/#\w+/);
            if (hashtag) initialText = `${hashtag[0]} progress update!`;
          }
          break;
        default:
          initialText = `Working on my game!\n\n#gamedev #indiedev`;
      }
    }

    return { initialText, finalTags, metadata };
  };

  const openPostModalForMission = (mission: Recommendation, initialMedia?: any | any[]) => {
    const mediaArray = Array.isArray(initialMedia) ? initialMedia : (initialMedia ? [initialMedia] : undefined);
    const { initialText, finalTags, metadata } = buildMissionPost(mission, mediaArray?.[0]);

    openPostModal({
      title: mission.title,
      translations: { en: { text: initialText } },
      trendId: metadata.trendId,
      type: mission.type,
      metadata: mission.metadata,
      media: mediaArray,
      tags: finalTags
    }, { forceEdit: true });
  };

  // --- Mission Start Handlers ---
  // Each mission type defines its own start behavior.
  // 'fetchMedia' types query AI for recommended media before opening PostModal.
  // 'trendModal' opens the TrendMediaModal for AI media matching.
  // 'openEmpty' opens PostModal directly with no media (for missions that don't need media analysis).
  // 'external' missions have no PostModal action (e.g. engagement links).
  // Per-type (and per-pillar) handler resolution
  const getMissionHandler = (mission: Recommendation): string => {
    const metadata = mission.metadata || {};

    // Pillar-specific overrides
    if (mission.type === 'CONTENT_PILLAR' && metadata.pillarId === 'gameplay') return 'gameplayModal';

    // Type-level defaults
    const typeHandlers: Record<string, string> = {
      TREND: 'trendModal',
      ENGAGEMENT: 'external',
      CONTENT_PILLAR: 'fetchMedia',
      DEVLOG: 'devlogModal',
      MEME: 'openEmpty',
      REPOST: 'fetchMedia',
      SEASONAL: 'openEmpty',
      INACTIVITY: 'openEmpty',
    };
    return typeHandlers[mission.type] || 'openEmpty';
  };

  const handleStartMission = async (mission: Recommendation) => {
    const metadata = mission.metadata || {};
    const handler = getMissionHandler(mission);

    switch (handler) {
      case 'trendModal':
        setTrendModalMission(mission);
        return;

      case 'devlogModal':
        setDevlogModalMission(mission);
        return;

      case 'gameplayModal':
        setGameplayModalMission(mission);
        return;

      case 'external':
        // Engagement missions have external links, no PostModal action
        return;

      case 'fetchMedia': {
        let initialMedia = null;
        try {
          const mediaResponse = await axios.get(`${API_BASE_URL}/recommendations/recommend-media`, {
            params: {
              missionType: mission.type,
              pillarId: metadata.pillarId,
              missionTitle: mission.title
            },
            headers: { Authorization: `Bearer ${token}` }
          });
          if (mediaResponse.data && !Array.isArray(mediaResponse.data) && mediaResponse.data.filePath) {
            initialMedia = mediaResponse.data;
          }
        } catch (err) {
          console.warn('[DailyMissions] Failed to fetch recommended media:', err);
        }
        openPostModalForMission(mission, initialMedia);
        return;
      }

      case 'openEmpty':
      default:
        openPostModalForMission(mission, null);
        return;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'TREND': return <TrendingUp className="h-4 w-4 text-primary" />;
      case 'SEASONAL': return <Sparkles className="h-4 w-4 text-purple-400" />;
      case 'ENGAGEMENT': return <MessageSquare className="h-4 w-4 text-green-400" />;
      case 'INACTIVITY': return <Clock className="h-4 w-4 text-orange-400" />;
      case 'REPOST': return <RefreshCw className="h-4 w-4 text-cyan-400" />;
      default: return <Calendar className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'TREND': return 'bg-primary/10 text-primary border-primary/20';
      case 'SEASONAL': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'ENGAGEMENT': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'INACTIVITY': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case 'REPOST': return 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20';
      default: return 'bg-slate-500/10 text-muted-foreground border-slate-500/20';
    }
  };

  if (loading) {
    return (
      <Card className={cn("bg-muted/50 border-border", className)}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500 animate-pulse" />
            Daily Missions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} h-20 w-full className="bg-card/50 rounded-xl" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("bg-muted/50 border-border overflow-hidden relative group h-fit", className)}>
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-500 via-purple-500 to-primary opacity-50" />
      
      <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-2">
            <div className="p-1.5 bg-primary/20 rounded-lg shadow-[0_0_15px_hsl(var(--primary)/0.2)]">
              <Flame className="h-4 w-4 text-primary" />
            </div>
            Daily Missions
            <Badge variant="outline" className="ml-2 bg-orange-500/5 text-orange-400 border-orange-500/20">
              {recommendations.filter(r => r.status === 'PENDING').length} Active
            </Badge>
          </CardTitle>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-3">
        {recommendations.length === 0 ? (
          <div className="py-8 text-center">
            <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">All missions completed for today!</p>
          </div>
        ) : (
          recommendations.map((mission) => (
            <div 
              key={mission.id}
              className={cn(
                "group/item relative p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02]",
                "bg-background/50 border-border/50 hover:border-border hover:bg-muted/80",
                mission.status === 'USED' ? 'opacity-50 grayscale' : 'opacity-100'
              )}
            >
              <div className="flex gap-3">
                <div className={cn("mt-1 shrink-0")}>
                  {mission.status === 'USED' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground group-hover/item:text-orange-500/50 transition-colors" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className={cn("text-[10px] uppercase tracking-wider px-1.5 py-0", getTypeColor(mission.type))}>
                      <span className="mr-1">{getTypeIcon(mission.type)}</span>
                      {mission.type.replace('_', ' ')}
                    </Badge>
                    {mission.priority >= 9 && (
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] px-1.5 py-0">URGENT</Badge>
                    )}
                  </div>
                  
                  <h4 className="text-sm font-bold text-foreground truncate group-hover/item:text-primary dark:group-hover/item:text-white transition-colors">
                    {mission.title}
                  </h4>
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
                    {mission.suggestion}
                  </p>

                  {mission.type === 'TREND' && mission.metadata?.template && (
                    <div className="mt-1.5 px-2 py-1.5 bg-primary/10 rounded-md border border-primary/20">
                      <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Format: </span>
                      <span className="text-xs font-mono text-primary/80">{mission.metadata.template}</span>
                    </div>
                  )}

                  {mission.status === 'PENDING' && isCommentMission(mission) ? (
                    <div className="mt-2 space-y-1.5">
                      {/* Comment list with external links */}
                      {mission.metadata?.comments?.map((comment: any) => (
                        <div key={comment.id} className="flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md px-2 py-1.5">
                          <span className="shrink-0">{getPlatformIcon(comment.platform, 'h-3 w-3')}</span>
                          <span className="font-semibold text-foreground shrink-0">@{comment.authorHandle || comment.authorName}</span>
                          <span className="truncate flex-1">{comment.text}</span>
                          {comment.postUrl && (
                            <a
                              href={comment.postUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-primary hover:text-primary/80 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/60 gap-1"
                        onClick={() => handleDismissMission(mission.id)}
                      >
                        <EyeOff className="h-3 w-3" /> Hide
                      </Button>
                    </div>
                  ) : mission.status === 'PENDING' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 mt-2 text-[11px] text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 gap-1 group-hover/item:translate-x-1 transition-all"
                      onClick={() => handleStartMission(mission)}
                    >
                      Start Mission <ArrowRight className="h-3 w-3" />
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
      
      <div className="px-6 py-3 bg-background/30 border-t border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-24 bg-card rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-orange-400 transition-all duration-1000" 
              style={{ width: `${(recommendations.filter(r => r.status === 'USED').length / Math.max(recommendations.length, 1)) * 100}%` }}
            />
          </div>
          <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
            {recommendations.filter(r => r.status === 'USED').length}/{recommendations.length} Done
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground font-medium italic">New missions in {timeLeft} hours</p>
      </div>

      {/* Trend Media Selection Modal */}
      <TrendMediaModal
        isOpen={!!trendModalMission}
        onClose={() => setTrendModalMission(null)}
        trendName={trendModalMission?.metadata?.trendName || ''}
        onSelectMedia={(media: TrendMediaMatch | TrendMediaMatch[]) => {
          setTrendModalMission(null);
          if (trendModalMission) {
            openPostModalForMission(trendModalMission, media);
          }
        }}
        onStartFromScratch={() => {
          setTrendModalMission(null);
          if (trendModalMission) {
            openPostModalForMission(trendModalMission);
          }
        }}
      />

      {/* Devlog Mission Context Modal */}
      <DevlogMissionModal
        isOpen={!!devlogModalMission}
        onClose={() => setDevlogModalMission(null)}
        gameId={gameId}
        onSelectMedia={(media: any) => {
          setDevlogModalMission(null);
          if (devlogModalMission) {
            openPostModalForMission(devlogModalMission, media);
          }
        }}
        onStartFromScratch={() => {
          setDevlogModalMission(null);
          if (devlogModalMission) {
            openPostModalForMission(devlogModalMission);
          }
        }}
      />

      {/* Gameplay Mission Context Modal */}
      <GameplayMissionModal
        isOpen={!!gameplayModalMission}
        onClose={() => setGameplayModalMission(null)}
        gameId={gameId}
        onSelectMedia={(media: any) => {
          setGameplayModalMission(null);
          if (gameplayModalMission) {
            openPostModalForMission(gameplayModalMission, media);
          }
        }}
        onStartFromScratch={() => {
          setGameplayModalMission(null);
          if (gameplayModalMission) {
            openPostModalForMission(gameplayModalMission);
          }
        }}
      />
    </Card>
  );
};
